import React, { useState, useEffect } from "react";
import {
    Box, Button, TextField, Typography, Grid, IconButton,
    Paper, Tooltip, Dialog, DialogTitle, DialogContent,
    DialogActions, CircularProgress,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

// ================================================================
// SHARED: Attachment Grid (view-only)
// ================================================================
const AttachmentGrid = ({ files = [], onPreview, height = "140px" }) => {
    if (!files.length) return null;
    return (
        <Box mt={2}>
            <Grid container spacing={2}>
                {files.map((att, index) => {
                    const src = att?.path || att?.preview || null;

                    const isImage =
                        att?.type?.startsWith("image") ||
                        /\.(jpg|jpeg|png|gif|webp)$/i.test(att?.path || "") ||
                        /\.(jpg|jpeg|png|gif|webp)$/i.test(att?.originalName || "") ||
                        /\.(jpg|jpeg|png|gif|webp)$/i.test(att?.fileName || "");

                    const isPdf =
                        att?.type === "application/pdf" ||
                        /\.pdf$/i.test(att?.path || "") ||
                        /\.pdf$/i.test(att?.originalName || "") ||
                        /\.pdf$/i.test(att?.fileName || "");

                    const displayName = att?.originalName || att?.fileName || att?.name || "File";

                    return (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <Paper
                                elevation={2}
                                sx={{
                                    p: 1, borderRadius: 2, position: "relative",
                                    overflow: "hidden", height: "100%",
                                    display: "flex", flexDirection: "column",
                                }}
                            >
                                {src && (
                                    <Tooltip title={isPdf ? "Open PDF" : "Preview Image"}>
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                isPdf
                                                    ? window.open(src, "_blank")
                                                    : onPreview && onPreview(src)
                                            }
                                            sx={{
                                                position: "absolute", top: 5, left: 5,
                                                zIndex: 2, background: "#fff",
                                                "&:hover": { background: "#f0f0f0" },
                                            }}
                                        >
                                            {isPdf
                                                ? <PictureAsPdfIcon fontSize="small" color="error" />
                                                : <ZoomInIcon fontSize="small" color="primary" />
                                            }
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {isImage ? (
                                    <img
                                        src={src}
                                        alt={displayName}
                                        onClick={() => src && onPreview && onPreview(src)}
                                        style={{
                                            width: "100%", height,
                                            objectFit: "cover", borderRadius: "8px",
                                            cursor: "pointer",
                                        }}
                                    />
                                ) : isPdf ? (
                                    src?.startsWith("blob:") ? (
                                        <Box
                                            sx={{ height, borderRadius: "8px", overflow: "hidden", cursor: "pointer" }}
                                            onClick={() => window.open(src, "_blank")}
                                        >
                                            <iframe
                                                src={src}
                                                title={displayName}
                                                style={{ width: "100%", height: "100%", border: "none", pointerEvents: "none" }}
                                            />
                                        </Box>
                                    ) : (
                                        <Box
                                            onClick={() => src && window.open(src, "_blank")}
                                            sx={{
                                                height, display: "flex", flexDirection: "column",
                                                alignItems: "center", justifyContent: "center",
                                                background: "#fff3f3", borderRadius: "8px",
                                                cursor: "pointer", border: "1px solid #ffcccc",
                                            }}
                                        >
                                            <PictureAsPdfIcon sx={{ fontSize: 50, color: "#e53935" }} />
                                            <Typography variant="caption" sx={{ color: "#e53935", mt: 0.5, fontWeight: 600 }}>
                                                Open PDF
                                            </Typography>
                                        </Box>
                                    )
                                ) : (
                                    <Box sx={{
                                        height, display: "flex", flexDirection: "column",
                                        alignItems: "center", justifyContent: "center",
                                        background: "#f5f5f5", borderRadius: "8px",
                                    }}>
                                        <ImageIcon sx={{ fontSize: 50, color: "#999" }} />
                                        <Typography variant="caption" sx={{ color: "#999", mt: 0.5 }}>
                                            {displayName.split(".").pop()?.toUpperCase() || "File"}
                                        </Typography>
                                    </Box>
                                )}

                                <Typography
                                    variant="caption"
                                    sx={{ mt: 1, display: "block", textAlign: "center", wordBreak: "break-word" }}
                                >
                                    {displayName}
                                </Typography>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
};

// ================================================================
// SHARED: Fullscreen image overlay
// ================================================================
const ImagePreviewOverlay = ({ src, onClose }) =>
    src ? (
        <Box
            onClick={onClose}
            sx={{
                position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                background: "rgba(0,0,0,0.85)", zIndex: 9999,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "zoom-out",
            }}
        >
            <IconButton onClick={onClose} sx={{ position: "absolute", top: 16, right: 16, color: "#fff" }}>
                <CloseIcon />
            </IconButton>
            <img
                src={src}
                alt="preview"
                style={{ maxWidth: "90%", maxHeight: "90%", borderRadius: 8, objectFit: "contain" }}
            />
        </Box>
    ) : null;

// ================================================================
// SHARED: Dispute Dialog
// ================================================================
const DisputeDialog = ({ state, onChange, onClose, onSubmit }) => (
    <Dialog
        open={state.open}
        onClose={!state.submitting ? onClose : undefined}
        maxWidth="sm"
        fullWidth
    >
        <DialogTitle sx={{ fontWeight: 700 }}>
            Raise Discrepancy
            <IconButton
                onClick={onClose}
                disabled={state.submitting}
                sx={{ position: "absolute", right: 10, top: 10 }}
            >
                <CloseIcon />
            </IconButton>
        </DialogTitle>

        <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" mb={2}>
                Please describe the issue clearly. Remark must be at least 20 characters.
            </Typography>
            <TextField
                fullWidth
                multiline
                label="Remark *"
                placeholder="Enter your discrepancy remark here..."
                value={state.remark}
                onChange={onChange}
                error={!!state.remarkError}
                helperText={state.remarkError}
                sx={{ "& .MuiInputBase-root": { alignItems: "flex-start", minHeight: "120px" } }}
                disabled={state.submitting}
            />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button variant="outlined" onClick={onClose} disabled={state.submitting}>
                Cancel
            </Button>
            <Button
                variant="contained"
                color="error"
                onClick={onSubmit}
                disabled={state.submitting}
                startIcon={
                    state.submitting
                        ? <CircularProgress size={16} color="inherit" />
                        : <ErrorOutlineIcon />
                }
            >
                {state.submitting ? "Submitting..." : "Submit Discrepancy"}
            </Button>
        </DialogActions>
    </Dialog>
);
const VerifyDialog = ({ state, onChange, onClose, onSubmit }) => (
    <Dialog open={state.open} onClose={!state.submitting ? onClose : undefined} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
            Verify Entry
            <IconButton onClick={onClose} disabled={state.submitting} sx={{ position: "absolute", right: 10, top: 10 }}>
                <CloseIcon />
            </IconButton>
        </DialogTitle>
        <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" mb={2}>
                Please add a verify remark. Remark must be at least 20 characters.
            </Typography>
            <TextField
                fullWidth multiline label="Verify Remark *"
                placeholder="Enter your verify remark here..."
                value={state.remark}
                onChange={onChange}
                error={!!state.remarkError}
                helperText={state.remarkError}
                sx={{ "& .MuiInputBase-root": { alignItems: "flex-start", minHeight: "120px" } }}
                disabled={state.submitting}
            />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button variant="outlined" onClick={onClose} disabled={state.submitting}>Cancel</Button>
            <Button
                variant="contained" color="success" onClick={onSubmit}
                disabled={state.submitting}
                startIcon={state.submitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
            >
                {state.submitting ? "Verifying..." : "Confirm Verify"}
            </Button>
        </DialogActions>
    </Dialog>
);

// ================================================================
// HELPERS
// ================================================================
const makeDisputeState = () => ({
    open: false,
    entryId: null,
    remark: "",
    remarkError: "",
    submitting: false,
});

const makeVerifyState = () => ({
    open: false,
    entryId: null,
    remark: "",
    remarkError: "",
    submitting: false,
});

const makeRemarkChangeHandler = (setter) => (e) => {
    const val = e.target.value;
    setter((prev) => ({
        ...prev,
        remark: val,
        remarkError:
            val.trim().replace(/\s+/g, " ").length < 20
                ? `Remark must be at least 20 characters (${val.trim().replace(/\s+/g, " ").length}/20)`
                : "",
    }));
};

// ================================================================
// MAIN COMPONENT
// ================================================================
const ViewToVerifyInvoiceOlympiadRC = ({ closePopup, modalData, invoiceType }) => {
    const [oldData, setOldData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [verifyingId, setVerifyingId] = useState(null);
    const [dispute, setDispute] = useState(makeDisputeState());
    const [verify, setVerify] = useState(makeVerifyState());
        const [attachmentMap, setAttachmentMap] = useState({});

    useEffect(() => {
        if (modalData?.regId) getOldData();
    }, [modalData?.regId]);

    // const getOldData = async () => {
    //     try {
    //         setHistoryLoading(true);
    //         const result = await window.Platform.database.getGenerateInvoiceDetails({
    //             regId: modalData?.regId,
    //             partyId: modalData?.partyId,
    //             invoiceType: "rc",
    //         });
    //         setOldData(result?.status ? result?.data || [] : []);
    //     } catch (error) {
    //         console.log(error);
    //         setOldData([]);
    //     } finally {
    //         setHistoryLoading(false);
    //     }
    // };

    const getOldData = async () => {
        try {
            setHistoryLoading(true);
            const result = await window.Platform.database.getGenerateInvoiceDetails({
                regId: modalData?.regId,
                partyId: modalData?.partyId,
                invoiceType: "rc",
            });

            const allData = result?.status ? result?.data || [] : [];

            // Only show entries that have RC invoice number filled
            // (reg_invoice_no is the definitive marker of an RC entry)
            const rcData = allData.filter(
                (item) => item?.reg_invoice_no && item.reg_invoice_no.toString().trim() !== ""
            );

            setOldData(rcData);
        } catch (error) {
            console.log(error);
            setOldData([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    // ── Verify entry ──
    const handleVerify = async (item) => {
        if (!window.confirm("Are you sure you want to verify this entry?")) return;
        try {
            setVerifyingId(item?.id);
            const result = await window.Platform.database.updateInvoicetatus({
                invoice_id: item?.id,
                action: "verify",
                invoiceType: "rc",
            });
            if (result?.status) {
                window.NotificationUtils.showSuccess(result?.message || "Verified successfully");
                closePopup();
            } else {
                window.NotificationUtils.showError(result?.message || "Something went wrong");
            }
        } catch (error) {
            console.log(error);
            window.NotificationUtils.showError("Something went wrong");
        } finally {
            setVerifyingId(null);
        }
    };

    // ── Dispute entry ──
    const openDispute = (item) => setDispute({ ...makeDisputeState(), open: true, entryId: item?.id });
    const closeDispute = () => setDispute(makeDisputeState());
    const handleRemarkChange = makeRemarkChangeHandler(setDispute);

    const handleDisputeSubmit = async () => {
        const trimmed = dispute.remark.trim();
        if (!trimmed) {
            setDispute((p) => ({ ...p, remarkError: "Remark is required" }));
            return;
        }
        if (trimmed.length < 20) {
            setDispute((p) => ({ ...p, remarkError: `Remark must be at least 20 characters (${trimmed.length}/20)` }));
            return;
        }
        try {
            setDispute((p) => ({ ...p, submitting: true }));
            const result = await window.Platform.database.updateInvoicetatus({
                invoice_id: dispute.entryId,
                action: "despute",
                despute_remark_rc: trimmed,
                invoiceType: "rc",
            });
            if (result?.status) {
                window.NotificationUtils.showSuccess(result?.message || "Discrepancy raised successfully");
                closeDispute();
                closePopup();
            } else {
                window.NotificationUtils.showError(result?.message || "Something went wrong");
                setDispute((p) => ({ ...p, submitting: false }));
            }
        } catch (error) {
            console.log(error);
            window.NotificationUtils.showError("Something went wrong");
            setDispute((p) => ({ ...p, submitting: false }));
        }
    };

       
  // ================= NEW FORM FILE UPLOAD =================
   // REMOVE these 3 functions entirely:
// handleFileUpload, handleRemoveAttachment, submitAttachment

// ADD these instead:
const handleFileUpload = (e, itemId) => {
    const files = Array.from(e.target.files);
    if (!files?.length) return;
    const mappedFiles = files.map((file) => ({
        file,
        name: file.name,
        type: file.type,
        preview: URL.createObjectURL(file),
    }));
    setAttachmentMap((prev) => ({
        ...prev,
        [itemId]: [...(prev[itemId] || []), ...mappedFiles],
    }));
    e.target.value = ""; // reset so same file can be re-picked
};

const handleRemoveAttachment = (itemId, indexToRemove) => {
    const isConfirm = window.confirm("Are you sure you want to remove this attachment?");
    if (!isConfirm) return;
    setAttachmentMap((prev) => ({
        ...prev,
        [itemId]: (prev[itemId] || []).filter((_, i) => i !== indexToRemove),
    }));
};

const submitAttachment = async (item) => {
    const files = attachmentMap[item?.id] || [];
    if (!files.length) return;

    const formPayload = new FormData();
    files.forEach((fileObj) => {
        if (fileObj?.file instanceof File) {
            formPayload.append("files", fileObj.file);
        }
    });
    formPayload.append("invoice_id", item?.id || "");

    const result = await window.Platform.database.updateInvoiceAttachmentsRC(formPayload);
    if (result?.status) {
        window.NotificationUtils.showSuccess(result?.message || "Added successfully");
        setAttachmentMap((prev) => ({ ...prev, [item?.id]: [] })); // clear only this item's files
        getOldData();
    } else {
        window.NotificationUtils.showError(result?.message || "Something went wrong");
    }
};


const openVerify = (item) => setVerify({ ...makeVerifyState(), open: true, entryId: item?.id });
const closeVerify = () => setVerify(makeVerifyState());
const handleVerifyRemarkChange = makeRemarkChangeHandler(setVerify);

const handleVerifySubmit = async () => {
    const trimmed = verify.remark.trim();
    if (!trimmed) {
        setVerify((p) => ({ ...p, remarkError: "Remark is required" }));
        return;
    }
    if (trimmed.length < 20) {
        setVerify((p) => ({ ...p, remarkError: `Remark must be at least 20 characters (${trimmed.length}/20)` }));
        return;
    }
    try {
        setVerify((p) => ({ ...p, submitting: true }));
        const result = await window.Platform.database.updateInvoicetatus({
            invoice_id: verify.entryId,
            action: "verify",
            verify_remark_rc: trimmed,       // ← RC-specific key
            invoiceType: "rc",
        });
        if (result?.status) {
            window.NotificationUtils.showSuccess(result?.message || "Verified successfully");
            closeVerify();
            closePopup();
        } else {
            window.NotificationUtils.showError(result?.message || "Something went wrong");
            setVerify((p) => ({ ...p, submitting: false }));
        }
    } catch (error) {
        console.log(error);
        window.NotificationUtils.showError("Something went wrong");
        setVerify((p) => ({ ...p, submitting: false }));
    }
};

    // ════════════════════════════════════════════════════════════
    // RENDER
    // ════════════════════════════════════════════════════════════
    return (
        <Box p={2} position="relative">
            <ImagePreviewOverlay src={previewImage} onClose={() => setPreviewImage(null)} />

            {/* Header */}
            <Typography variant="h5" fontWeight={700} textAlign="center" mb={1}>
                Verify Invoice
            </Typography>
            <Box sx={{ borderBottom: "1px solid #000", mb: 2, pb: 1 }}>
                <Typography sx={{ fontSize: "15px" }}>
                    <b>RNO ID:</b> {modalData?.rno_id} |
                    <b style={{ marginLeft: 10 }}>RM Name:</b> {modalData?.userName} |
                    <b style={{ marginLeft: 10 }}>Party Name:</b> {modalData?.schoolName} ({modalData?.partyId})
                </Typography>
            </Box>
            <IconButton onClick={closePopup} sx={{ position: "absolute", top: 12, right: 12 }}>
                <CloseIcon />
            </IconButton>

            {/* Entries */}
            {historyLoading ? (
                <Box textAlign="center" py={4}>
                    <CircularProgress />
                </Box>
            ) : oldData.length === 0 ? (
                <Typography textAlign="center" color="text.secondary" py={4}>
                    No entries found.
                </Typography>
            ) : (
                oldData.map((item, index) => {
                    const displayIndex = oldData.length - index;
                    const invoiceAttachments = item?.reg_invoice_attachment || [];
                    const isVerifying = verifyingId === item?.id;
                    const isDisabled = item?.is_dispute_rc == 1 || item?.is_verify_rc == 1;

                    return (
                        <Paper
                            key={item?.id || index}
                            elevation={2}
                            sx={{ p: 2.5, mb: 3, background: "#fafafa", border: "1px solid #e0e0e0", borderRadius: 2 }}
                        >
                            <Typography variant="h6" fontWeight={700} mb={2}>
                                Entry {displayIndex}
                            </Typography>

                            {/* ── Invoice Section ── */}
                            <Box mb={2}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth size="small" disabled
                                            label="Invoice Number"
                                            value={item?.reg_invoice_no || ""}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth size="small" disabled
                                            label="Invoice Amount"
                                            value={item?.reg_invoice_amount || ""}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth size="small" disabled
                                            type="date"
                                            label="Invoice Date"
                                            value={
                                                item?.reg_invoice_date
                                                    ? new Date(item?.reg_invoice_date).toLocaleDateString("en-CA")
                                                    : ""
                                            }
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            startIcon={<UploadFileIcon />}
                                        >
                                            Upload Attachment
                                            <input
                                                hidden
                                                multiple
                                                type="file"
                                                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                                                onChange={(e) => handleFileUpload(e, item?.id)}
                                            />
                                        </Button>
                                        {(attachmentMap[item?.id] || []).length > 0 && (
                                            <Button
                                                variant="contained"
                                                color="success"
                                                size="small"
                                                onClick={() => submitAttachment(item)}
                                            >
                                                Submit
                                            </Button>
                                        )}
                                    </Box>
                                    {(attachmentMap[item?.id] || []).length > 0 && (
                                        <AttachmentGrid
                                            files={attachmentMap[item?.id]}
                                            onRemove={(index) => handleRemoveAttachment(item?.id, index)}
                                            height="140px"
                                        />
                                    )}
                                </Grid>

                                {invoiceAttachments.length > 0 && (
                                    <AttachmentGrid
                                        files={invoiceAttachments}
                                        onPreview={setPreviewImage}
                                        height="160px"
                                    />
                                )}
                            </Box>

                            {/* ── Action row ── */}
                            {isDisabled ? (
                                // <span className="mt-3">
                                //     Discrepancy Remark: <span style={{ color: "#ff0000" }}>{item?.despute_remark_rc}</span>
                                // </span>
                                <Box className="mt-3">
                                    <Typography variant="subtitle2" fontSize={14} fontWeight={600} mb={1}>
                                        Add RC Remark : <span style={{ color: "#1a29d4" }}> {item?.remark} </span>
                                    </Typography>
                                    <Typography variant="subtitle2" fontSize={14} fontWeight={600} mb={1}>
                                        Discrepancy Remark : <span style={{ color: "#ff0000" }}> {item?.despute_remark_rc} </span>
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <Box gap={1} mt={1}>
                                        <span>
                                            Add RC Remark : <span style={{ color: "#1a29d4" }}> {item?.remark} </span>
                                        </span>
                                    </Box>
                                    <Box
                                        display="flex" justifyContent="space-between" gap={2}
                                        mt={2.5} pt={1.5} borderTop="1px solid #e0e0e0"
                                    >
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="success"
                                        size="small"
                                        startIcon={
                                            isVerifying
                                                ? <CircularProgress size={16} color="inherit" />
                                                : <CheckCircleOutlineIcon />
                                        }
                                        onClick={() => openVerify(item)}
                                        disabled={isDisabled}
                                    >
                                        {isVerifying ? "Verifying..." : "Verify"}
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="error"
                                        size="small"
                                        startIcon={<ErrorOutlineIcon />}
                                        onClick={() => openDispute(item)}
                                        disabled={isVerifying}
                                    >
                                        Discrepancy
                                    </Button>
                                </Box>
                                </>
                            )}
                        </Paper>
                    );
                })
            )}
<VerifyDialog
    state={verify}
    onChange={handleVerifyRemarkChange}
    onClose={closeVerify}
    onSubmit={handleVerifySubmit}
/>
            <DisputeDialog
                state={dispute}
                onChange={handleRemarkChange}
                onClose={closeDispute}
                onSubmit={handleDisputeSubmit}
            />
        </Box>
    );
};

export default ViewToVerifyInvoiceOlympiadRC;